/**
 * Opt-in tracing for the answer's arrival animation.
 *
 * Whether the answer visibly streams depends on a sequence nobody can see from
 * the outside: when the card mounts, when `answer_type` lands, when isStreaming
 * flips, and whether the text arrives in pieces or all at once. Those differ per
 * answer type and per backend timing, so this prints the sequence instead of
 * leaving it to inference.
 *
 * Turn on in the browser console, then ask one question:
 *
 *     localStorage.setItem('debugChatAnim', '1'); location.reload();
 *
 * and off again with:
 *
 *     localStorage.removeItem('debugChatAnim');
 *
 * Costs nothing when off: every call returns before touching arguments.
 */

let enabled = null;

const isOn = () => {
  if (enabled === null) {
    try {
      enabled = typeof window !== 'undefined'
        && window.localStorage?.getItem('debugChatAnim') === '1';
    } catch {
      enabled = false; // storage blocked (private mode, embedded frames)
    }
  }
  return enabled;
};

let started = 0;

/** Milliseconds since the first traced event, so the sequence reads as timing. */
const stamp = () => {
  if (!started) started = Date.now();
  return `+${String(Date.now() - started).padStart(5, ' ')}ms`;
};

export const animTrace = (event, detail) => {
  if (!isOn()) return;
  // eslint-disable-next-line no-console
  console.log(`%c[anim] ${stamp()} ${event}`, 'color:#7c6bff;font-weight:600', detail ?? '');
};

/** Report the environment facts that silently disable animation entirely. */
export const animTraceEnvironment = () => {
  if (!isOn()) return;
  const reduced = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  animTrace('environment', {
    prefersReducedMotion: !!reduced,
    note: reduced ? 'motion is suppressed system-wide; nothing here will animate' : 'motion allowed',
  });
};

export const isAnimDebugOn = isOn;
